import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function RefundPolicy() {
    const [animateIn, setAnimateIn] = useState(false)

    useEffect(() => {
        const t = setTimeout(() => setAnimateIn(true), 50)
        return () => clearTimeout(t)
    }, [])

    const pageFade = animateIn ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'

    return (
        <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
            <Navbar />

            {/* Split Premium Frame Layout */}
            <div className="flex flex-col lg:flex-row flex-1 select-none">

                {/* Left Side: Policy Highlight Anchor Panel */}
                <div className="lg:w-[35%] bg-brand-navy flex-shrink-0 relative overflow-hidden flex flex-col justify-between p-8 lg:p-12 text-white min-h-[200px] lg:min-h-full">
                    <div className="absolute inset-0 pointer-events-none"
                        style={{ background: 'radial-gradient(circle at bottom left, rgba(212,168,67,0.05) 0%, transparent 60%)' }} />

                    <div className="my-auto max-w-xs relative z-10">
                        <p className="text-brand-amber text-[10px] font-body uppercase tracking-widest mb-3">
                            Billing Protocol
                        </p>
                        <h2 className="font-display font-bold text-2xl lg:text-3xl leading-tight mb-4">
                            Subscription Control Terms
                        </h2>
                        <p className="font-body text-xs leading-relaxed text-gray-400 mb-6">
                            GMC uses database-driven plan metrics linked securely with Stripe. Users manage fulfillment schedules, tier changes, and processing cycles entirely at their own discretion.
                        </p>

                        <div className="space-y-3.5 border-t border-white/10 pt-6">
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-amber mt-1.5 flex-shrink-0" />
                                <p className="font-body text-xs text-gray-300">
                                    <strong className="text-white block font-sans font-semibold mb-0.5">Self-Managed Dashboard</strong>
                                    Instant cancellation mechanisms are accessible directly from your account profile settings.
                                </p>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-amber mt-1.5 flex-shrink-0" />
                                <p className="font-body text-xs text-gray-300">
                                    <strong className="text-white block font-sans font-semibold mb-0.5">Continuous Service</strong>
                                    Cancellations stop future invoices. Active resources persist until your current cycle ends.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Stripe Security Token Footer Badge */}
                    <div className="relative z-10 flex items-center gap-2 text-gray-500 pt-6 border-t border-white/5 lg:border-t-0">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                            stroke="#D4A843" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <span className="text-[10px] font-sans font-semibold tracking-wide uppercase">
                            Secured via Stripe Interface
                        </span>
                    </div>
                </div>

                {/* Right Side: Primary Policy Copy Content Sheet */}
                <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:p-16">
                    <div className={'w-full max-w-2xl mx-auto transition-all duration-700 ease-out ' + pageFade}>

                        {/* Heading & Metadata Pass */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-gray-100 rounded-full shadow-sm mb-4">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                <span className="text-[10px] font-sans font-semibold tracking-wider text-gray-500 uppercase">
                                    Fulfillment Guidelines
                                </span>
                            </div>
                            <h1 className="font-sans font-bold text-3xl text-gray-950 tracking-tight mb-2">
                                GMC Refund & Cancellation Policy
                            </h1>
                            <p className="text-xs font-body text-gray-400">
                                Effective Date: June 2026 • Stripe Infrastructure Standard
                            </p>
                        </div>

                        {/* Luigi's Exact Copy Blocks */}
                        <div className="space-y-6 text-sm font-body text-gray-600 leading-relaxed">

                            <p className="font-medium text-gray-900 bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                                Users are responsible for managing their own subscriptions through their GMC account.
                            </p>

                            <div className="relative border-l-2 border-brand-amber pl-4 my-6">
                                <p className="font-sans font-bold text-gray-900 uppercase tracking-wide text-xs mb-1">
                                    Cancellation Continuity
                                </p>
                                <p>
                                    Subscriptions may be cancelled at any time. Cancellation will prevent future billing and access will continue until the end of the current paid billing period.
                                </p>
                            </div>

                            <p>
                                Because GMC is a digital subscription service, fees paid for a billing period are generally non-refundable once access to the platform has been granted.
                            </p>

                            <p className="font-semibold text-gray-900">
                                GMC is not responsible for cancelling subscriptions on behalf of users and users remain responsible for managing their own subscription status.
                            </p>

                            <p>
                                In exceptional circumstances, GMC may, at its sole discretion, review refund requests on a case-by-case basis.
                            </p>

                            <div className="bg-gray-100 text-gray-500 rounded-xl p-4 border border-gray-200/60 font-sans text-xs font-medium tracking-wide leading-relaxed uppercase">
                                Nothing in this policy affects any rights that cannot be excluded under applicable law.
                            </div>

                        </div>

                        {/* Back-Navigation Trigger */}
                        <div className="mt-10 pt-6 border-t border-gray-200 flex items-center justify-between">
                            <Link to="/register"
                                className="inline-flex items-center gap-1 text-xs font-sans font-bold text-gray-900 hover:text-gray-700 transition-colors">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="19" y1="12" x2="5" y2="12" strokeDasharray="none"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Return to Account Creation
                            </Link>

                            <span className="text-[11px] text-gray-400 font-body">
                                admin@guardianmessagecheck.com
                            </span>
                        </div>

                    </div>
                </div>

            </div>

            <Footer />
        </div>
    )
}